import React, { forwardRef } from 'react';
import { Line } from 'react-chartjs-2';

// Brand palette in the Word report's series order: navy, gold, grey, light
// blue, mauve, sage green, teal, slate.
export const IP_CHART_PALETTE = ['#012341', '#F3BD48', '#A6A6A6', '#599BE3', '#AA8090', '#A9D08E', '#337B91', '#5E819C'];

/**
 * Generic multi-series line chart for the Investment Perspectives report,
 * styled to match the Word version: light grey panel, centred title inside the
 * chart area, brand palette, legend beneath.
 *
 * data = { dates: ['YYYY-MM-DD', ...], series: [{ label, values:[...], colour?, dash? }] }
 * valueFormat: 'percent' appends %, otherwise a plain number.
 * timeUnit: x-axis tick unit — 'year' (default, multi-year series) or 'month'
 * (YTD charts get Jan-26/Feb-26/... tags).
 * decimals: value decimal places for axis ticks and tooltips (default 0, as Word).
 * title: rendered centred inside the grey panel (replaces the old external caption).
 */
const IpLineChart = forwardRef(({ data, valueFormat = 'number', timeUnit = 'year', decimals = 0, title = null }, ref) => {
  if (!data || !Array.isArray(data.series) || data.series.length === 0 || !Array.isArray(data.dates)) {
    return <div className="chart-placeholder">No data available</div>;
  }

  const dates = data.dates;
  const datasets = data.series.map((s, i) => ({
    label: s.label,
    data: dates.map((d, j) => ({ x: d, y: s.values?.[j] ?? null })),
    borderColor: s.colour || IP_CHART_PALETTE[i % IP_CHART_PALETTE.length],
    backgroundColor: 'transparent',
    borderWidth: 1.6,
    pointRadius: 0,
    tension: 0.15,
    spanGaps: true,
    borderDash: s.dash ? [5, 4] : undefined,
  }));

  const fmt = (v) => (v == null ? '—' : (valueFormat === 'percent' ? `${Number(v).toFixed(decimals)}%` : Number(v).toFixed(decimals)));

  // Yearly axes: start the axis at the first data point's year boundary so the
  // opening year gets a tick label (data starting late-January otherwise sits
  // just past the year tick and the first year goes unlabelled).
  const xMin = timeUnit === 'year' && dates.length
    ? `${String(dates[0]).slice(0, 4)}-01-01`
    : undefined;

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    layout: { padding: 0 },
    interaction: { intersect: false, mode: 'index' },
    plugins: {
      legend: {
        position: 'bottom',
        align: 'center',
        labels: { font: { size: 10 }, boxWidth: 18, boxHeight: 2, padding: 8, color: '#212529' },
      },
      tooltip: {
        mode: 'index', intersect: false,
        callbacks: { label: (ctx) => `${ctx.dataset.label}: ${fmt(ctx.parsed.y)}` },
      },
    },
    scales: {
      x: {
        type: 'time',
        min: xMin,
        time: { unit: timeUnit, tooltipFormat: 'MMM yyyy', displayFormats: { month: 'MMM-yy' } },
        border: { display: false },
        grid: { display: false },
        ticks: { font: { size: 10 }, color: '#212529', maxTicksLimit: timeUnit === 'month' ? 13 : 11, maxRotation: 0, minRotation: 0 },
      },
      y: {
        border: { display: false },
        grid: { color: 'rgba(0,0,0,0.10)' },
        ticks: { font: { size: 10 }, color: '#212529', callback: (val) => fmt(val) },
      },
    },
  };

  return (
    <div className="ip-chart-panel">
      {title && <div className="ip-chart-title">{title}</div>}
      <div style={{ flex: 1, minHeight: 0, position: 'relative' }}>
        <Line ref={ref} data={{ datasets }} options={options} />
      </div>
    </div>
  );
});

IpLineChart.displayName = 'IpLineChart';
export default IpLineChart;
